// src/pages/CaseHistoryPage/caseHistoryDrafts.js
/**
 * Offline drafts for interview sections, in IndexedDB.
 *
 * A deliberately separate database from `speechTherapyOffline`: adding a store
 * there would mean bumping that database's version, and a failed upgrade would
 * put the queue of unsent recordings at risk. Those cannot be recreated; a form
 * draft can.
 */

const DB_NAME = 'speechTherapyIntake';
const STORE = 'sectionDrafts';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const draftId = (caseId, sectionKey) => `${caseId}:${sectionKey}`;

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

/** Keep a section that could not be saved to the server. */
export async function saveDraft(caseId, sectionKey, answers) {
  const db = await openDB();
  const item = { id: draftId(caseId, sectionKey), caseId, sectionKey, answers, saved_at: Date.now() };
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').put(item);
    req.onsuccess = () => resolve(item);
    req.onerror = () => reject(req.error);
  });
}

export async function getDrafts(caseId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readonly').getAll();
    req.onsuccess = () => resolve((req.result || []).filter((d) => !caseId || d.caseId === caseId));
    req.onerror = () => reject(req.error);
  });
}

export async function deleteDraft(caseId, sectionKey) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').delete(draftId(caseId, sectionKey));
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function countDrafts(caseId) {
  return (await getDrafts(caseId)).length;
}
